import type { Lot, State } from '../types';
import { VIEW_MODES, apiUrl, log } from './common';
import { updateBidInPlace, updateLogoInPlace } from '../polling';

export async function refreshLotBidsRemote(
  id: string,
  retry: boolean,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined,
  render: () => void,
  DEBUG: boolean
): Promise<void> {
  let token: string | null;
  try {
    token = await ensureAccessToken(Boolean(retry));
  } catch {
    throw new Error('Unable to acquire access token.');
  }
  if (!token) throw new Error('Unable to acquire access token.');
  const endpoint = apiUrl('extensions/scrapes/' + encodeURIComponent(id) + '/refresh-bids');
  if (DEBUG) log.debug('POST', endpoint);
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: '{}',
  });
  if (DEBUG) log.debug('POST status', response.status, 'ct', response.headers.get('content-type'));
  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return refreshLotBidsRemote(
      id,
      true,
      state,
      ensureAccessToken,
      mapRemoteDocument,
      render,
      DEBUG
    );
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to refresh bids (' + response.status + ')');
  }
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct && ct.indexOf('json') === -1) throw new Error('Unexpected response from API.');
  const doc = await response.json();
  const mapped = mapRemoteDocument(doc);
  if (mapped) {
    const isSuperAdminView = state.viewMode === VIEW_MODES.SUPERADMIN;
    if (!isSuperAdminView) {
      const currentUserId =
        state && state.user && typeof state.user.sub === 'string' ? state.user.sub : null;
      const ownerId =
        mapped && mapped.userOverlay && typeof mapped.userOverlay.userId === 'string'
          ? mapped.userOverlay.userId
          : null;
      if (currentUserId && ownerId && ownerId !== currentUserId) {
        if (DEBUG)
          log.debug('Ignoring bid refresh for other user lot', ownerId, '!=', currentUserId);
        return;
      }
    }
    const idx = state.lots.findIndex(x => x.id === mapped.id);
    if (idx >= 0) state.lots[idx] = mapped;
    else state.lots.unshift(mapped);

    // Try in-place DOM update first to avoid full page re-render
    // This prevents image flicker and improves performance
    const inPlaceSuccess = updateBidInPlace(mapped.id, mapped);
    if (!inPlaceSuccess) {
      // Fall back to full render only if in-place update failed
      render();
    }
  }
}

export async function refreshAuctioneerLogoRemote(
  id: string,
  retry: boolean,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined,
  render: () => void,
  DEBUG: boolean
): Promise<void> {
  let token: string | null;
  try {
    token = await ensureAccessToken(Boolean(retry));
  } catch {
    throw new Error('Unable to acquire access token.');
  }
  if (!token) throw new Error('Unable to acquire access token.');
  const endpoint = apiUrl(
    'extensions/scrapes/' + encodeURIComponent(id) + '/refresh-auctioneer-logo'
  );
  if (DEBUG) log.debug('POST', endpoint);
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: '{}',
  });
  if (DEBUG) log.debug('POST status', response.status, 'ct', response.headers.get('content-type'));
  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return refreshLotBidsRemote(
      id,
      true,
      state,
      ensureAccessToken,
      mapRemoteDocument,
      render,
      DEBUG
    );
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to refresh auctioneer logo (' + response.status + ')');
  }
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct && ct.indexOf('json') === -1) throw new Error('Unexpected response from API.');
  const doc = await response.json();
  const mapped = mapRemoteDocument(doc);
  if (mapped) {
    const isSuperAdminView = state.viewMode === VIEW_MODES.SUPERADMIN;
    if (!isSuperAdminView) {
      const currentUserId =
        state && state.user && typeof state.user.sub === 'string' ? state.user.sub : null;
      const ownerId =
        mapped && mapped.userOverlay && typeof mapped.userOverlay.userId === 'string'
          ? mapped.userOverlay.userId
          : null;
      if (currentUserId && ownerId && ownerId !== currentUserId) {
        if (DEBUG)
          log.debug('Ignoring bid refresh for other user lot', ownerId, '!=', currentUserId);
        return;
      }
    }
    const idx = state.lots.findIndex(x => x.id === mapped.id);
    if (idx >= 0) {
      state.lots[idx] = mapped;

      // Try in-place DOM update first to avoid full page re-render
      const inPlaceSuccess = updateLogoInPlace(mapped.id, mapped.auctioneerLogoUrl);
      if (!inPlaceSuccess) {
        // Fall back to full render only if in-place update failed
        render();
      }
    }
  }
}
